import { Link, useNavigate, useParams } from "react-router-dom";
import { Button } from "#/components/common/Button";
import { Card } from "#/components/common/Card";
import { ErrorState } from "#/components/common/ErrorState";
import { PageLoader } from "#/components/common/PageLoader";
import { StatusBadge } from "#/components/common/StatusBadge";
import { useRole } from "../hooks";
import { ArrowLeft } from "lucide-react";

export default function RoleViewPage() {
  const { roleId } = useParams();
  const id = Number(roleId);
  const navigate = useNavigate();
  const roleQuery = useRole(id);

  if (!Number.isFinite(id) || id <= 0) {
    return (
      <ErrorState
        title="Invalid role"
        message="The selected role id is invalid."
      />
    );
  }

  if (roleQuery.isLoading) {
    return <PageLoader label="Loading role..." />;
  }

  if (roleQuery.isError || !roleQuery.data) {
    return (
      <ErrorState
        title="Failed to load role"
        message={
          roleQuery.error instanceof Error
            ? roleQuery.error.message
            : "Could not load the requested role."
        }
      />
    );
  }

  const role = roleQuery.data;
  const permissions = role.permissionCodes ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 hover:underline"
          >
            <ArrowLeft className="h-4 w-4" />
            Go back
          </button>

          <h1 className="mt-3 text-2xl font-bold text-slate-900">
            Role details
          </h1>
          <p className="text-sm text-slate-500">
            Review role setup and permission assignments.
          </p>
        </div>

        <Link to={`/admin/roles/${id}/edit`}>
          <Button>Edit role</Button>
        </Link>
      </div>

      <Card className="p-6">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <p className="text-xs uppercase text-slate-500">Name</p>
            <p className="mt-1 text-sm font-semibold text-slate-900">
              {role.name}
            </p>
          </div>

          <div>
            <p className="text-xs uppercase text-slate-500">Code</p>
            <p className="mt-1 text-sm font-semibold text-slate-900">
              {role.code}
            </p>
          </div>

          <div className="md:col-span-2">
            <p className="text-xs uppercase text-slate-500">Description</p>
            <p className="mt-1 text-sm text-slate-900">
              {role.description || "-"}
            </p>
          </div>

          <div>
            <p className="text-xs uppercase text-slate-500">Status</p>
            <div className="mt-2">
              <StatusBadge
                label={role.active ? "Active" : "Inactive"}
                tone={role.active ? "success" : "neutral"}
              />
            </div>
          </div>

          <div>
            <p className="text-xs uppercase text-slate-500">Permissions</p>
            <p className="mt-1 text-sm text-slate-900">{permissions.length}</p>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h2 className="text-lg font-semibold text-slate-900">
          Assigned permissions
        </h2>

        {permissions.length === 0 ? (
          <p className="mt-3 text-sm text-slate-500">
            No permissions are currently assigned to this role.
          </p>
        ) : (
          <div className="mt-4 flex flex-wrap gap-2">
            {permissions.map((permission) => (
              <span
                key={permission}
                className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700"
              >
                {permission}
              </span>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
